import { useEffect, useRef } from 'react'
import {
  getStoredToken,
  getStoredRefreshToken,
  msUntilExpiry,
  refreshAccessToken,
} from '../api/refresh-token'

// Refresh this many seconds before the token's `exp`, so a SHORT token is renewed before
// it lapses. Only relevant when the token expires sooner than REVOCATION_POLL_MS.
const DEFAULT_BUFFER_SECONDS = 60

/**
 * How often to ask the server whether this session is still allowed — the real reason
 * this hook exists.
 *
 * The access token's own expiry does NOT drive this. An account blocked by an admin is
 * only discoverable by asking: QL-User's /api/v4/refresh 401s once the account is no
 * longer ACTIVE. So this interval IS the detection window for a block, and it has to
 * hold even for a token with 90 days left on it — which is exactly the local setup, and
 * why exp-based scheduling alone was wrong here.
 *
 * Raising this lengthens how long a blocked admin keeps UI access; lowering it multiplies
 * load on /api/v4/refresh, which does a DB read plus Redis on every call. 60s is the
 * agreed trade-off.
 */
const REVOCATION_POLL_MS = 60 * 1000

// Floor. A token shorter than the buffer would otherwise compute a delay at or near 0,
// refresh, get another short token, and tight-loop the endpoint.
const MIN_REFRESH_DELAY_MS = 15_000

// How long to wait after a transient failure (server down, offline) before trying again.
// Same as the poll interval: there is no benefit to retrying faster, and a tight retry
// loop against a dead server is its own kind of storm.
const UNAVAILABLE_RETRY_MS = REVOCATION_POLL_MS

const msUntilRefresh = (token: string, bufferSeconds: number): number | null => {
  const ms = msUntilExpiry(token)
  if (ms === null) return null
  return Math.max(ms - bufferSeconds * 1000, 0)
}

interface Options {
  /** Called when the session can no longer be kept alive (refresh failed). */
  onExpired?: () => void
  /** Called with each newly minted access token, so callers can sync React state. */
  onRefreshed?: (token: string) => void
  bufferSeconds?: number
}

/**
 * Polls `/api/v4/refresh` every REVOCATION_POLL_MS while the app is open, which does two
 * jobs at once:
 *
 *   1. **Detects revocation** (the primary purpose). An admin blocking this account is
 *      only discoverable by asking the server — the refresh 401s once the account is not
 *      ACTIVE — so the poll interval is the logout delay for a blocked admin. This has to
 *      work even when the access token itself is valid for 90 days.
 *   2. **Keeps a short token alive**, by refreshing early if `exp` arrives before the
 *      next poll would.
 *
 * A transient failure never logs the admin out; only a definitive 401/403 does.
 *
 * Renders nothing, and no-ops when there is no stored token (e.g. on /login).
 */
export const useProactiveTokenRefresh = ({
  onExpired,
  onRefreshed,
  bufferSeconds = DEFAULT_BUFFER_SECONDS,
}: Options = {}): void => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Hold the latest callbacks in refs so changing them cannot re-arm the effect (which
  // would cancel and rebuild the timer on every render).
  const onExpiredRef = useRef<Options['onExpired']>(onExpired)
  const onRefreshedRef = useRef<Options['onRefreshed']>(onRefreshed)
  onExpiredRef.current = onExpired
  onRefreshedRef.current = onRefreshed

  useEffect(() => {
    let cancelled = false
    let stopped = false

    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    // Every scheduling path goes through here, so none can hand setTimeout a raw value.
    const armTimer = (delay: number) => {
      clear()
      if (cancelled || stopped) return
      timerRef.current = setTimeout(() => void runRefresh(), Math.max(delay, MIN_REFRESH_DELAY_MS))
    }

    const scheduleFrom = (token: string | null) => {
      clear()
      if (cancelled || stopped || !token) return
      const expBased = msUntilRefresh(token, bufferSeconds)
      // Whichever comes FIRST: the revocation poll, or renewing a token that expires
      // sooner than the poll would fire. A 90-day token therefore still gets checked
      // every 60 seconds, while a 90-second token is renewed at 30s.
      //
      // Taking the min also removes any need for a 32-bit setTimeout ceiling — the
      // delay can never exceed REVOCATION_POLL_MS.
      const delay =
        expBased === null ? REVOCATION_POLL_MS : Math.min(expBased, REVOCATION_POLL_MS)
      armTimer(delay)
    }

    const runRefresh = async () => {
      if (cancelled || stopped || !getStoredRefreshToken()) return
      // force: this is the revocation check, so it must reach the server. Letting the
      // cooldown answer from cache would skip the check entirely.
      const outcome = await refreshAccessToken('proactive', { force: true })
      if (cancelled) return

      if (outcome.status === 'refreshed') {
        onRefreshedRef.current?.(outcome.token)
        // Re-arm from the token we JUST received, not a re-read of storage.
        scheduleFrom(outcome.token)
        return
      }

      if (outcome.status === 'revoked') {
        // Definitive 401/403: the account was blocked, or the refresh token is dead.
        // This is the case the poll exists to catch. Stop and log out once.
        stopped = true
        onExpiredRef.current?.()
        return
      }

      // 'unavailable' — the server could not answer (5xx, timeout, offline). That says
      // nothing about whether the session is valid, so KEEP it and try again. Logging
      // out here would eject an admin mid-article on any network blip, every 60 seconds.
      armTimer(UNAVAILABLE_RETRY_MS)
    }

    // On refocus — covers the machine sleeping straight past `exp`, where the timer
    // fired late or not at all.
    const onFocus = () => {
      if (cancelled || stopped) return
      if (typeof document !== 'undefined' && document.hidden) return
      const token = getStoredToken()
      // Token gone but a refresh token remains → refresh now; otherwise reschedule.
      if (!token && getStoredRefreshToken()) void runRefresh()
      else scheduleFrom(token)
    }

    scheduleFrom(getStoredToken())
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)

    return () => {
      cancelled = true
      clear()
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [bufferSeconds])
}
